/* eslint-disable @typescript-eslint/no-unused-vars */
import axios, { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Api } from '../axiosApi';

export interface IListagemContasReceber {
  idrec: string;
  emissao: Date;
  vencimento: Date;
  numero: string;
  venda: number;
  logo:string;
  vrecebido: number;
  desconto: number;
  acrescimo: number;
  parceiros: string;
  vparcela: number;
  marcado: boolean;
  vreceber: number
  // O que é esse lançamento (ex.: "Mensalidade do sistema referente a
  // setembro/2026") — usado como referência no comprovante de pagamento
  // depois da baixa (ver ComprovanteBaixa.ts).
  historico?: string;
  // Quantas contas antigas foram juntadas nessa conta (0 quando não é o
  // "container" de uma consolidada) — ver ConsolidacaoReceber.ts, backend.
  qtdtitulos?: number;
  // true quando essa linha é uma das parcelas (2ª em diante) de uma
  // consolidação dividida em N parcelas — não é o container (qtdtitulos = 0)
  // nem uma filha fechada, mas deve aparecer marcada como "Consolidado"
  // igual o container (ver GetAll.ts, backend).
  parcelaconsolidada?: boolean;
  // Fração "i/N" da parcela dentro do grupo consolidado (ex.: "2/3") — só
  // relevante quando parcelaconsolidada é true.
  parcela?: string | null;
  // true quando a conta já foi baixada — usado pra não mostrar "Vencida"
  // numa conta que já está paga (ver GetAll.ts, backend).
  status?: boolean;
  // Só vem preenchido na busca por tipo 'recebidas' (contas pagas por
  // período) — data em que o recebimento aconteceu de verdade.
  datarecebimento?: Date;
}
export interface IMarcados{
  idrec: string;
	marcado: boolean;
	desconto: number;
	vreceber: number;
}
export interface ICheque{
  idcheque: string;
  banco: string;
  agencia: string;
  conta: string;
  datadeposito: Date;
  titular: string;
  valor: number;
  numero: string;
}
type TotalCount = {
    data: IListagemContasReceber[];
    totalCount: number;
    selecionado: number;
    total: number;
}
interface IRecebidos {
  recebido: number;
  total: number;
  idconta: string;
  datacaixa: Date;
  tipocaixa: boolean;
  formasPagamento?: { idoperacao: string; valor: number; categoria?: string; idcontadestino?: string; datacompensacao?: string }[];
}
export interface IContasaReceber {
  idparceiro: string;
  idplanoconta: string;
  vencimento: Date;
  emissao: Date;
  parcela?: string;
  numero?: string;
  venda: number;
  vparcela: number;
  vrecebido: number;
  acrescimo: number;
  juros: number;
  desconto: number;
  vreceber: number;
  // Vincula parcelas geradas juntas num mesmo lançamento parcelado (ver
  // handleSave em DetalhesDeReceber.tsx) — mesmo idlote em todas as
  // parcelas dessa leva.
  idlote?: string;
}
export interface IContasaReceberUpdate {
  idparceiro: string;
  idplanoconta: string;
  vencimento: Date;
  emissao: Date;
  parcela?: string;
  numero?: string;
  venda: number;
  vparcela: number;
  vrecebido: number;
  acrescimo: number;
  juros: number;
  desconto: number;
  vreceber: number;
  recebido: number;
}
export interface IContaConsolidadaFilho {
  numero: string;
  parcela: string | null;
  emissao: Date;
  vencimento: Date;
  juros: number;
  desconto: number;
  acrescimo: number;
  venda: number;
  vparcela: number;
  vrecebido: number;
  // Valor que ainda faltava receber no momento em que essa parcela foi
  // juntada (backend: `valorconsolidado`).
  valor: number;
}

// Uma parcela irmã (mesmo idlote) do lançamento parcelado que essa conta faz
// parte — ver `outrasParcelas` abaixo e a regra de edição em
// DetalhesDeReceber.tsx (uma vez que qualquer parcela do grupo tenha
// recebimento, as demais ficam com edição restrita).
export interface IParcelaDoLote {
  idrec: string;
  parcela: string | null;
  numero?: string | null;
  vencimento: Date;
  vparcela: number;
  vrecebido: number;
  vreceber: number;
  status: boolean;
  desconto: number;
  acrescimo: number;
}

interface IGetReceber {
 idparceiro: string;
  idplanoconta: string;
  vencimento: Date;
  emissao: Date;
  parcela?: string;
  numero?: string;
  venda: number;
  vparcela: number;
  vrecebido: number;
  acrescimo: number;
  juros: number;
  desconto: number;
  vreceber: number;
  recebido: number;
  tipo: number;
  // Só vem preenchido quando essa conta é uma consolidada (ver
  // ConsolidacaoReceber.ts, backend) — as parcelas originais juntadas nela.
  filhos?: IContaConsolidadaFilho[];
  // Vincula parcelas geradas juntas num mesmo lançamento parcelado — não
  // existe quando o lançamento é de parcela única.
  idlote?: string;
  // Demais parcelas do mesmo idlote, só vem preenchido quando idlote existe.
  outrasParcelas?: IParcelaDoLote[];
}

export interface IResultadoJuntarContas {
  idrec: string;
  numero: string;
}

export interface IParcelaConsolidada {
  vencimento: Date;
  valor: number;
}

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const create = async (dados: IContasaReceber): Promise<string | Error> => {
  try {
    const guuid = uuidv4();
    const tipo = 2;
    // Sem "Número Nota" informado, cada lançamento recebe seu próprio número
    // gerado a partir do uuid (idrec) desse mesmo registro — não um valor
    // compartilhado entre parcelas de um mesmo lote (cada create() aqui gera
    // um guuid novo, então cada parcela sai com um número diferente).
    const numero = dados.numero?.trim()
      ? dados.numero
      : String(parseInt(guuid.replace(/-/g, '').slice(0, 8), 16));
    const { data } = await Api.post<IContasaReceber>('/contasreceber',  {idrec: guuid, tipo: tipo,  ...dados, numero});

    if (Object.keys(data).length === 0) {
      return guuid;
    }
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const updateById = async (id: string, dados: IContasaReceberUpdate): Promise<void | Error> => {
  try {

    await Api.put(`/contasreceber/${id}`, dados);

  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

// Troca a quantidade de parcelas de um lançamento já salvo (apaga o grupo
// antigo inteiro e cria as novas parcelas no lugar) — só é aceito pelo
// backend enquanto nenhuma parcela do grupo tiver recebimento (ver
// RegenerarParcelas.ts, backend, e a regra de edição em DetalhesDeReceber.tsx).
const regenerarParcelas = async (id: string, parcelas: (IContasaReceber & { idrec: string; tipo: number })[]): Promise<void | Error> => {
  try {
    await Api.put(`/contasreceber/${id}/parcelas`, { parcelas });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const getAll = async (page = 1, filter = '', tipo = '', datai = '', dataf = ''): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = `/contasreceber?page=${page}&limit=${300}&filter=${filter}&tipo=${tipo}&datai=${datai}&dataf=${dataf}`;
    const { data, headers } = await Api.get(urlRelativa);  
   
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count']|| 0),
        selecionado: Number(headers['x-total-selecionado'] || 0),
        total: Number(headers['x-total-valor'] || 0),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const Marcados = async (dados: IMarcados[]): Promise<void | Error> => {
  try {
    await Api.post('/recebermarcado', dados);    
    return;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const Juntar = async (idsContas: string[], parcelas: IParcelaConsolidada[]): Promise<IResultadoJuntarContas | Error> => {
  try {
    const { data } = await Api.post<IResultadoJuntarContas>('/contasreceber/juntar', { idsContas, parcelas });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
// Recebido cobre o total? Compara em centavos: o recebido vem da soma das
// formas de pagamento (ex.: 3 × 1750,65 = 5251,950000000001 no JS), e o `===`
// mandava uma baixa integral pra baixa parcial — que deixava a conta em
// aberto/"Vencida" com saldo zero (contas a receber do bjl).
const quitaOTotal = (recebido: number, total: number) =>
  Math.round(Number(recebido) * 100) === Math.round(Number(total) * 100);

const Baixa = async (dados: IRecebidos, receber: IListagemContasReceber[]): Promise<void | Error> => {
  try {
    let historicos = '';
    if(quitaOTotal(dados.recebido, dados.total)){
      const baixa = receber.map(conta => {
        historicos += `${conta.numero}, `;
        return {
          idrec: conta.idrec,
          idconta: dados.idconta,
          datacaixa: dados.datacaixa,
          recebido: dados.recebido,
          historico: `Recebimento dos numeros ${historicos.slice(0, -2)}`,
          tipocaixa: dados.tipocaixa,
          total: dados.total,
          formasPagamento: dados.formasPagamento,
        };
      });
      await Api.post('/receberbaixa', baixa);
      return;
    }else{
      const baixa = receber.map(conta => {
        return {
          idrec: conta.idrec,
          idconta: dados.idconta,
          datacaixa: dados.datacaixa,
          dataoperacao: dados.datacaixa,
          recebido: dados.recebido,
          tipocaixa: dados.tipocaixa,
          total: dados.total,
          formasPagamento: dados.formasPagamento,
        };
      });
      await Api.post('/receberbaixaparcial', baixa);
      return;
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const BaixaCheque = async (dados: Omit<IRecebidos, 'idconta' | 'tipocaixa'>, receber: IListagemContasReceber[], cheque: ICheque[]): Promise<void | Error> => {
  try {
    if(quitaOTotal(dados.recebido, dados.total)){
      const baixa = receber.map(conta => {
        return {
          idreceber: conta.idrec,
          dataoperacao: dados.datacaixa,
          recebido: conta.vreceber,
          historico: '',
          tipocaixa: true,
          total: dados.total,
        };
      });
      await Api.post('/receberbaixacheque',  [baixa, cheque]); 
      return;
    }else{
      const baixa = receber.map(conta => {
        return {
          idreceber: conta.idrec,
          datacaixa: dados.datacaixa,
          recebido: dados.recebido,
          valornfe: 0,
          historico: '',
          tipocaixa: true,
          total: dados.total,
        };
      });
      await Api.post('/receberbaixachequeparcial', [baixa, cheque]); 
      return;   
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const getById = async (id: string): Promise<IGetReceber | Error> => {
  try {
    const { data } = await Api.get(`/contasreceber/${id}`);
    if (data) {
      return data;
    }

    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
export interface IFormaPagamentoHistorico {
  historico: string;
  valor: number;
}
export interface IRecebidoHistorico {
  idrecebida: string;
  dataoperacao: Date;
  valor: number;
  idoperacao: string;
  nomeConta: string;
  nomeParceiro: string;
  numero: string;
  formasPagamento: IFormaPagamentoHistorico[];
}
// Histórico de baixas (totais e parciais) de um título — usado pra
// reimprimir o comprovante de uma baixa já feita (ver DetalhesDeReceber.tsx).
const getRecebidos = async (idrec: string): Promise<IRecebidoHistorico[] | Error> => {
  try {
    const { data } = await Api.get(`/contasreceber/${idrec}/recebidos`);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o histórico de recebimentos.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
export interface IParcelaPorVenda {
  idrec: string;
  parcela: string | null;
  numero: string;
  vencimento: Date;
  vparcela: number;
  vreceber: number;
  vrecebido: number;
  status: boolean;
  cliente: string;
  cpf: string;
  logo: string | null;
  datapagamento: Date | null;
  formapagamento: string | null;
}
// Todas as parcelas (contasreceber) de uma venda específica — usado pra
// montar o carnê de pagamento na tela de Detalhes de Venda (ver
// impressaocarne.ts). Uma venda à vista devolve 1 linha só, a prazo devolve N.
const getPorVenda = async (idmov: string): Promise<IParcelaPorVenda[] | Error> => {
  try {
    const { data } = await Api.get(`/contasreceber/porvenda/${idmov}`);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar as parcelas da venda.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const deleteById = async (id: string): Promise<void | Error> => {
  try {
    await Api.delete(`/contasreceber/${id}`);
  }catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
export const ContasReceberService = {
  getAll,
  getPorVenda,
  Marcados,
  Baixa,
  BaixaCheque,
  Juntar,
  create,
  updateById,
  getById,
  getRecebidos,
  deleteById,
  regenerarParcelas,
};